import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FaChartBar, FaChartLine, FaDownload, FaPrint } from "react-icons/fa";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { obtenerReporteFinancieroComparativo } from "../../../services/contabilidad/reportesService";
import { formatCurrency, periodoActual } from "../utils";

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const CHART_COLORS = ["#4a4a4a", "#dc3545", "#198754"];

const ReportesFinancieros = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [periodo, setPeriodo] = useState(periodoActual());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const periodoAnt = (p) => { const [y, m] = p.split("-").map(Number); const d = new Date(y, m - 2, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

  const cargar = useCallback(async () => {
    if (!rucEmpresa) return;
    try { setLoading(true); setError(""); setData(await obtenerReporteFinancieroComparativo({ rucEmpresa, periodoActual: periodo, periodoAnterior: periodoAnt(periodo) })); }
    catch (e) { setError("No se pudo cargar el reporte financiero."); }
    finally { setLoading(false); }
  }, [rucEmpresa, periodo]);

  useEffect(() => { cargar(); }, [cargar]);

  if (!rucEmpresa) return <Alert variant="warning" className="border-0 shadow-sm">No se encontró RUC de empresa.</Alert>;

  const { actual, anterior, variacion } = data || {};

  const chartData = [
    { name: "Ventas", valor: actual?.pnl?.ingresos || 0 },
    { name: "Gastos", valor: actual?.pnl?.gastos || 0 },
    { name: "Utilidad", valor: actual?.pnl?.utilidad || 0 },
  ];

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}

      {/* Barra de acción */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={1} className="text-center">
              <div className="rounded-3 p-3 d-inline-block" style={BTN}><FaChartBar size={20} /></div>
            </Col>
            <Col md={4}>
              <h5 className="mb-1 fw-semibold">Análisis de Resultados</h5>
              <p className="text-muted mb-0 small">Comparativa mensual de ingresos, gastos y utilidad.</p>
            </Col>
            <Col md={3}>
              <Form.Control type="month" className="border-0 shadow-sm py-2 fw-bold" style={{ borderRadius: "10px" }} value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
            </Col>
            <Col md={4} className="d-flex gap-2 justify-content-end">
              <Button variant="light" className="rounded-pill px-3 fw-semibold text-muted" onClick={() => window.print()}>
                <FaPrint className="me-2" />Imprimir
              </Button>
              <Button className="rounded-pill px-3 border-0 shadow-sm fw-bold" style={BTN}>
                <FaDownload className="me-2" />Exportar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPI cards — igual que ListaNegra */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", color: "white", borderRadius: "15px" }}>
              <FaChartLine size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{formatCurrency(actual?.pnl?.ingresos || 0)}</h4>
              <p className="mb-0 small opacity-75">Ingresos</p>
              <small className={`opacity-75 ${(variacion?.ingresos || 0) >= 0 ? "" : "text-warning"}`}>
                {(variacion?.ingresos || 0) >= 0 ? "+" : ""}{formatCurrency(variacion?.ingresos || 0)} vs ant.
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
              <FaChartLine size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{formatCurrency(actual?.pnl?.gastos || 0)}</h4>
              <p className="mb-0 small opacity-75">Gastos</p>
              <small className="opacity-75">{(variacion?.gastos || 0) >= 0 ? "+" : ""}{formatCurrency(variacion?.gastos || 0)} vs ant.</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
              <FaChartLine size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{formatCurrency(actual?.pnl?.utilidad || 0)}</h4>
              <p className="mb-0 small opacity-75">Utilidad</p>
              <small className="opacity-75">{(variacion?.utilidad || 0) >= 0 ? "+" : ""}{formatCurrency(variacion?.utilidad || 0)} vs ant.</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
            <Card.Body className="text-center py-4" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", color: "white", borderRadius: "15px" }}>
              <FaChartLine size="2rem" className="mb-2" />
              <h4 className="fw-bold mb-0">{formatCurrency(actual?.cashflow?.neto || 0)}</h4>
              <p className="mb-0 small opacity-75">Flujo Neto</p>
              <small className="opacity-75">{(variacion?.flujoNeto || 0) >= 0 ? "+" : ""}{formatCurrency(variacion?.flujoNeto || 0)} vs ant.</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        {/* Gráfica */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <div style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6", padding: "12px 16px" }}>
              <span className="fw-semibold text-secondary small text-uppercase">Distribución de Resultados</span>
            </div>
            <Card.Body className="d-flex align-items-center justify-content-center" style={{ minHeight: 320 }}>
              {loading ? <Spinner animation="border" variant="secondary" /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: "10px", border: "none", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }} />
                    <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                      {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Tabla comparativa */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <div style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6", padding: "12px 16px" }}>
              <span className="fw-semibold text-secondary small text-uppercase">Resumen Comparativo</span>
            </div>
            <div className="table-responsive">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="secondary" /></div>
              ) : (
                <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.95rem" }}>
                  <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    <tr>
                      <th className="py-3 px-4 text-secondary fw-semibold">Concepto</th>
                      <th className="py-3 px-4 text-secondary fw-semibold text-end">Actual</th>
                      <th className="py-3 px-4 text-secondary fw-semibold text-end">Anterior</th>
                      <th className="py-3 px-4 text-secondary fw-semibold text-center">Var.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Ingresos Operat.", actual?.pnl?.ingresos, anterior?.pnl?.ingresos, variacion?.ingresos, true],
                      ["Gastos / Costos", actual?.pnl?.gastos, anterior?.pnl?.gastos, variacion?.gastos, false],
                      ["Utilidad Neta", actual?.pnl?.utilidad, anterior?.pnl?.utilidad, variacion?.utilidad, true],
                      ["Activos Totales", actual?.balance?.activos, anterior?.balance?.activos, null, true],
                      ["Pasivos Totales", actual?.balance?.pasivos, anterior?.balance?.pasivos, null, false],
                    ].map(([label, curr, prev, vari, positiveIsGood]) => (
                      <tr key={label} style={{ borderBottom: "1px solid #f1f3f4" }}>
                        <td className="px-4 py-3 fw-medium">{label}</td>
                        <td className="px-4 py-3 text-end fw-bold">{formatCurrency(curr || 0)}</td>
                        <td className="px-4 py-3 text-end text-muted">{formatCurrency(prev || 0)}</td>
                        <td className="px-4 py-3 text-center">
                          {vari != null ? (
                            <Badge pill bg={(positiveIsGood ? vari >= 0 : vari <= 0) ? "success" : "danger"}>
                              {Math.abs(vari).toFixed(2)}
                            </Badge>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ReportesFinancieros;
